<template>
  <div
    class="bg-white dark:bg-gray-800 sm:rounded-lg sm:shadow dark:sm:shadow-gray-900/50 relative"
  >
    <div
      ref="gridRef"
      class="grid-scroll overflow-y-auto overflow-x-auto -mr-4 pr-4 touch-pan-x touch-pan-y"
      :style="{ maxHeight: maxHeight }"
    >
      <table
        ref="tableRef"
        class="min-w-full w-full table-fixed divide-y divide-gray-200 dark:divide-gray-700"
      >
        <thead class="bg-white dark:bg-gray-800 sticky top-0 z-30">
          <tr>
            <th
              class="px-2 sm:px-4 py-2 sm:py-3 text-left text-[10px] sm:text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider w-28 sm:w-40 md:w-56 lg:w-64 sticky left-0 z-40 bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700"
            >
              Collega
            </th>
            <th
              class="px-2 sm:px-3 py-2 sm:py-3 text-center text-[10px] sm:text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider w-16 sm:w-20 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors select-none border-r border-gray-200 dark:border-gray-700"
              @click="$emit('sort-change')"
              title="Klik om te sorteren"
            >
              <div class="flex items-center justify-center gap-1">
                <span>Besch.</span>
                <component
                  :is="sortDirection === 'desc' ? ChevronDownIcon : ChevronUpIcon"
                  class="h-3 w-3"
                />
              </div>
            </th>
            <th
              v-for="(w, idx) in displayedWeeks"
              :key="idx"
              class="px-0.5 sm:px-1 py-2 text-center text-[10px] font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider min-w-[32px] sm:min-w-0 cursor-help"
              :title="weekRangeTitle(w)"
            >
              {{ w.iso_label }}
            </th>
          </tr>
        </thead>
        <tbody class="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
          <!-- Loading state -->
          <tr v-if="loading">
            <td :colspan="displayedWeeks.length + 2" class="px-4 py-12 text-center">
              <div class="flex flex-col items-center justify-center">
                <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                <p class="mt-2 text-gray-600 dark:text-gray-400">Beschikbaarheid laden…</p>
              </div>
            </td>
          </tr>
          <!-- Employee rows -->
          <template v-else>
            <tr v-for="emp in employees" :key="emp.simplicate_id">
              <td
                class="px-2 sm:px-4 py-1.5 sm:py-2 whitespace-nowrap sticky left-0 z-10 bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700"
              >
                <div class="text-xs sm:text-sm font-medium text-gray-900 dark:text-white truncate">
                  {{ emp.name }}
                </div>
                <template v-if="emp.simplicate_id !== SWP_EMPLOYEE_ID">
                  <div class="text-[10px] sm:text-xs text-gray-500 dark:text-gray-400 truncate">
                    {{ emp.function || '—' }}
                  </div>
                  <div class="text-[10px] sm:text-xs text-gray-400 dark:text-gray-500">
                    {{ parseFloat(emp.fte_ratio) || 1.0 }}FTE
                  </div>
                </template>
                <div v-else class="text-[10px] sm:text-xs text-indigo-500 dark:text-indigo-400">
                  Externe partner
                </div>
              </td>
              <td
                class="px-2 sm:px-3 py-1.5 sm:py-2 text-center border-r border-gray-200 dark:border-gray-700"
              >
                <div
                  class="text-xs sm:text-sm font-semibold text-gray-900 dark:text-gray-100"
                  :title="
                    emp.simplicate_id === SWP_EMPLOYEE_ID
                      ? `Gemiddeld ${averageAvailability(emp).toFixed(1)} dagen ingezet per week`
                      : `Gemiddeld ${averageAvailability(emp).toFixed(1)} dagen beschikbaar per week`
                  "
                >
                  {{ averageAvailability(emp).toFixed(1) }}
                </div>
              </td>
              <td
                v-for="(w, idx) in displayedWeeks"
                :key="idx"
                class="px-0.5 py-0.5 sm:py-1.5 text-center align-middle"
              >
                <button
                  class="relative w-full h-10 sm:h-10 min-w-[30px] sm:min-w-0 rounded-md text-[9px] sm:text-[10px] font-semibold text-gray-900 dark:text-gray-100 shadow-inner cursor-pointer transition-all duration-150 hover:scale-105 hover:ring-2 hover:ring-blue-500 hover:ring-offset-1 hover:z-10"
                  :style="cellStyle(emp, w)"
                  @click="$emit('cell-click', { employee: emp, week: w })"
                  :title="tooltipText(emp, w)"
                >
                  <!-- Proposed indicator (voorstellen + beide modes) -->
                  <span
                    v-if="showProposedIndicator && hasProposedAssignment(emp, w)"
                    class="absolute -top-1 right-[2px] h-3 w-3 rounded-full bg-yellow-400 ring-2 ring-white dark:ring-gray-800"
                    aria-label="Voorgesteld deze week"
                    title="Voorgesteld deze week"
                  />
                  <!-- Presourced indicator (pre-sourcing + beide modes) -->
                  <span
                    v-if="showPresourcedIndicator && hasProspectAssignment(emp, w)"
                    class="absolute -top-1 -right-1 z-10 h-3 w-3 rounded-full bg-purple-600 ring-2 ring-white dark:ring-gray-800"
                    aria-label="Gepresourced deze week"
                    title="Gepresourced deze week"
                  />
                  {{ displayValue(emp, w) }}
                </button>
              </td>
            </tr>
          </template>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount, nextTick, watch } from 'vue'
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/vue/24/solid'
import { useContractStatus } from '../../composables/domain/useContractStatus.js'
import { SWP_EMPLOYEE_ID } from '@/constants/swp.js'

const { formatContractEndDate } = useContractStatus()

// Format YYYY-MM-DD → dd-mm-yyyy for the week-header hover tooltip
const formatDateFull = input => {
  if (!input) return ''
  const s = String(input).split('T')[0]
  const d = new Date(`${s}T00:00:00`)
  if (isNaN(d)) return s
  return `${String(d.getDate()).padStart(2, '0')}-${String(d.getMonth() + 1).padStart(2, '0')}-${d.getFullYear()}`
}
const weekRangeTitle = w => {
  if (!w?.week_start || !w?.week_end) return ''
  return `Week ${w.iso_label ?? w.iso_week}: ${formatDateFull(w.week_start)} t/m ${formatDateFull(w.week_end)}`
}

const props = defineProps({
  employees: { type: Array, required: true },
  // weeks: [{ week_start, week_end, iso_week, iso_year }]
  weeks: { type: Array, required: true },
  loading: { type: Boolean, default: false },
  sortDirection: { type: String, default: 'desc' }, // 'asc' or 'desc'
  availabilityMode: { type: String, default: 'presourcing' }
})

defineEmits(['cell-click', 'sort-change'])

// Indicator dots follow the selected availability mode:
// - purple (presourced) shows when assignments are subtracted → presourcing / voorgesteld
// - yellow (proposed) shows when proposals are subtracted → alleen_voorstellen / voorgesteld
const showPresourcedIndicator = computed(
  () => props.availabilityMode === 'presourcing' || props.availabilityMode === 'voorgesteld'
)
const showProposedIndicator = computed(
  () => props.availabilityMode === 'alleen_voorstellen' || props.availabilityMode === 'voorgesteld'
)

// Mobile detection
const isMobile = ref(false)
const checkMobile = () => {
  isMobile.value = window.innerWidth < 640
}

// Limit weeks on mobile to 10
const displayedWeeks = computed(() => {
  if (isMobile.value && props.weeks.length > 10) {
    return props.weeks.slice(0, 10)
  }
  return props.weeks
})

// Color scale: green(#16a34a) -> yellow(#eab308) -> red(#b91c1c)
const stops = [
  { v: 0, c: '#16a34a' },
  { v: 2, c: '#eab308' },
  { v: 4, c: '#b91c1c' }
]

// Basic hex color interpolation without external deps
const hexToRgb = hex => {
  const clean = hex.replace('#', '')
  const bigint = parseInt(clean, 16)
  return {
    r: (bigint >> 16) & 255,
    g: (bigint >> 8) & 255,
    b: bigint & 255
  }
}
const rgbToHex = ({ r, g, b }) => {
  const toHex = n => n.toString(16).padStart(2, '0')
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`
}
const lerp = (a, b, t) => a + (b - a) * t
const lerpColor = (c1, c2, t) => {
  const a = hexToRgb(c1)
  const b = hexToRgb(c2)
  const mix = {
    r: Math.round(lerp(a.r, b.r, t)),
    g: Math.round(lerp(a.g, b.g, t)),
    b: Math.round(lerp(a.b, b.b, t))
  }
  return rgbToHex(mix)
}

const colorForValue = val => {
  if (val < 0) return '#3b82f6' // blue-500 for negative (PM overboeking)
  const v = Math.max(0, Math.min(4, val ?? 0))
  const s0 = stops[0],
    s1 = stops[1],
    s2 = stops[2]
  if (v <= s1.v) {
    const t = (v - s0.v) / (s1.v - s0.v || 1)
    return lerpColor(s0.c, s1.c, t)
  } else {
    const t = (v - s1.v) / (s2.v - s1.v || 1)
    return lerpColor(s1.c, s2.c, t)
  }
}

const findWeekData = (emp, week) => {
  return emp.weeks.find(w => w.week_start === week.week_start)
}

const displayValue = (emp, week) => {
  const wd = findWeekData(emp, week)
  // SWP: show booked assignment days instead of available days
  if (emp.simplicate_id === SWP_EMPLOYEE_ID) {
    const assignmentDays = (wd?.assignment_hours ?? 0) / 8
    return assignmentDays.toFixed(1)
  }
  return (wd?.available_days ?? 0).toFixed(1)
}

const tooltipText = (emp, week) => {
  const wd = findWeekData(emp, week)
  if (!wd) return ''

  // SWP: show booked projects
  if (emp.simplicate_id === SWP_EMPLOYEE_ID) {
    const assignmentDays = (wd.assignment_hours ?? 0) / 8
    let tooltip = `Samenwerkingspartner: ${assignmentDays.toFixed(1)}d ingezet`
    if (wd.assignments && wd.assignments.length > 0) {
      const projectNames = wd.assignments
        .map(
          a =>
            `${a.prospect_subject}${a.organization_name ? ` (${a.organization_name})` : ''}: ${a.dpw}d/w`
        )
        .join('\n')
      tooltip += `\n\nProjecten:\n${projectNames}`
    }
    return tooltip
  }

  // If week is before contract start date, show specific message
  if (wd.is_before_start_date) {
    return `Niet beschikbaar - in dienst vanaf ${formatContractEndDate(emp.contract_start_date)}`
  }

  // If week is past contract end date, show specific message
  if (wd.is_past_end_date) {
    return `Niet beschikbaar - uit dienst per ${formatContractEndDate(emp.contract_end_date)}`
  }

  const availableDays = Number(wd.available_days || 0)
  const availableHours = (availableDays * 8).toFixed(1)
  const fteRatio = parseFloat(emp.fte_ratio) || 1.0
  // Use the per-week capacity so partial contract weeks show the correct
  // workday-pro-rated capacity. Fall back for older API responses.
  const maxDaysPerWeek =
    wd.weekly_capacity_days != null
      ? Number(wd.weekly_capacity_days)
      : parseFloat(emp.max_days_per_week) || fteRatio * 5 * ((emp.max_billability || 100) / 100)
  const capacityHours = (maxDaysPerWeek * 8).toFixed(1)

  let tooltip =
    availableDays < 0
      ? `${Math.abs(availableHours)}u overbooked (PM) bij ${capacityHours}u/week (${fteRatio}FTE)`
      : `${availableHours}u beschikbaar van ${capacityHours}u/week (${fteRatio}FTE)`

  // Show assignment details with project names
  if (wd.assignments && wd.assignments.length > 0) {
    const projectNames = wd.assignments
      .map(
        a =>
          `${a.prospect_subject}${a.organization_name ? ` (${a.organization_name})` : ''}: ${a.dpw}d/w`
      )
      .join('\n')
    tooltip += `\n\nGepresourced op:\n${projectNames}`
  } else if (wd.assignment_hours) {
    tooltip += `, ${wd.assignment_hours.toFixed(1)}u gepresourced`
  }

  // Show proposed assignment details
  if (wd.proposed_assignments && wd.proposed_assignments.length > 0) {
    const proposedNames = wd.proposed_assignments
      .map(
        a =>
          `${a.prospect_subject}${a.organization_name ? ` (${a.organization_name})` : ''}: ${a.dpw}d/w`
      )
      .join('\n')
    tooltip += `\n\nVoorgesteld op:\n${proposedNames}`
  } else if (wd.proposed_hours) {
    tooltip += `, ${wd.proposed_hours.toFixed(1)}u voorgesteld`
  }

  return tooltip
}

const cellStyle = (emp, week) => {
  const wd = findWeekData(emp, week)

  // SWP: indigo color scale based on booked assignment days (0=light, 5=dark)
  // Always set explicit text color so it works in both light and dark mode
  if (emp.simplicate_id === SWP_EMPLOYEE_ID) {
    const assignmentDays = (wd?.assignment_hours ?? 0) / 8
    if (assignmentDays <= 0) return { backgroundColor: '#e0e7ff', color: '#3730a3' } // indigo-100 bg, indigo-800 text
    const t = Math.min(1, assignmentDays / 5)
    // Interpolate from indigo-200 to indigo-500
    const from = hexToRgb('#c7d2fe')
    const to = hexToRgb('#6366f1')
    const mix = {
      r: Math.round(lerp(from.r, to.r, t)),
      g: Math.round(lerp(from.g, to.g, t)),
      b: Math.round(lerp(from.b, to.b, t))
    }
    // Dark text for light backgrounds, white text for dark backgrounds
    const textColor = t > 0.5 ? '#eef2ff' : '#3730a3'
    return { backgroundColor: rgbToHex(mix), color: textColor }
  }

  // If week is before contract start date, show gray striped pattern
  if (wd?.is_before_start_date) {
    return {
      backgroundColor: '#e5e7eb', // gray-200
      backgroundImage:
        'repeating-linear-gradient(45deg, transparent, transparent 3px, rgba(156, 163, 175, 0.3) 3px, rgba(156, 163, 175, 0.3) 6px)',
      opacity: 0.7
    }
  }

  // If week is past contract end date, show gray striped pattern
  if (wd?.is_past_end_date) {
    return {
      backgroundColor: '#e5e7eb', // gray-200
      backgroundImage:
        'repeating-linear-gradient(45deg, transparent, transparent 3px, rgba(156, 163, 175, 0.3) 3px, rgba(156, 163, 175, 0.3) 6px)',
      opacity: 0.7
    }
  }

  // Negative available_days (PM overboeking): blue background
  if ((wd?.available_days ?? 0) < 0) {
    return { backgroundColor: '#93c5fd', color: '#1e3a5f' } // blue-300 bg, dark blue text
  }

  const base = colorForValue(wd?.available_days ?? 0)
  // apply slight lightening effect manually
  const { r, g, b } = hexToRgb(base)
  const lighten = x => Math.min(255, Math.round(x + (255 - x) * 0.15))
  const lightHex = rgbToHex({ r: lighten(r), g: lighten(g), b: lighten(b) })
  return { backgroundColor: lightHex }
}
const hasProspectAssignment = (emp, week) => {
  const wd = findWeekData(emp, week)
  return !!(wd && Number(wd.assignment_hours) > 0)
}

const hasProposedAssignment = (emp, week) => {
  const wd = findWeekData(emp, week)
  return !!(wd && Number(wd.proposed_hours) > 0)
}

// Calculate average availability (or average booked days for SWP)
const averageAvailability = emp => {
  if (!emp.weeks || emp.weeks.length === 0) return 0
  // SWP: show average booked assignment days
  if (emp.simplicate_id === SWP_EMPLOYEE_ID) {
    const total = emp.weeks.reduce((sum, w) => sum + (parseFloat(w.assignment_hours) || 0) / 8, 0)
    return total / emp.weeks.length
  }
  const total = emp.weeks.reduce((sum, w) => sum + (parseFloat(w.available_days) || 0), 0)
  return total / emp.weeks.length
}

const gridRef = ref(null)
const tableRef = ref(null)
const maxHeight = ref('none')

const calculateMaxHeight = () => {
  if (!gridRef.value) return
  const rect = gridRef.value.getBoundingClientRect()
  const availableHeight = window.innerHeight - rect.top - 16 // 16px buffer from bottom
  maxHeight.value = `${availableHeight}px`
}

onMounted(() => {
  checkMobile()
  nextTick(() => {
    calculateMaxHeight()
  })
  window.addEventListener('resize', calculateMaxHeight)
  window.addEventListener('resize', checkMobile)
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', calculateMaxHeight)
  window.removeEventListener('resize', checkMobile)
})

// Recalculate when employees change (data loaded)
watch(
  () => props.employees,
  () => {
    nextTick(() => {
      calculateMaxHeight()
    })
  }
)
</script>

<style scoped>
.grid-scroll {
  /* keep scrolling inside grid area */
  overscroll-behavior: contain;
  overflow-x: hidden; /* no horizontal scroll; table fits width */
  scrollbar-width: auto; /* Firefox thickness */
  scrollbar-color: #94a3b8 #f1f5f9; /* thumb track */
}
.grid-scroll::-webkit-scrollbar {
  height: 14px; /* horizontal */
  width: 14px; /* vertical */
}
.grid-scroll::-webkit-scrollbar-thumb {
  background-color: #94a3b8; /* slate-400 */
  border-radius: 9999px;
  border: 3px solid #e2e8f0; /* slate-200 */
}
.grid-scroll::-webkit-scrollbar-track {
  background: #f1f5f9; /* slate-100 */
}

/* Dark mode scrollbar */
:global(.dark) .grid-scroll {
  scrollbar-color: #4b5563 #374151; /* gray-600 gray-700 */
}
:global(.dark) .grid-scroll::-webkit-scrollbar-thumb {
  background-color: #4b5563; /* gray-600 */
  border-color: #374151; /* gray-700 */
}
:global(.dark) .grid-scroll::-webkit-scrollbar-track {
  background: #374151; /* gray-700 */
}
</style>
